export interface DiffResult {
  key: string;
  desired: any;
  applied: any;
}

/**
 * Campos que o dispositivo BWS 4G não devolve de forma legível na leitura de configuração
 * (write-only ou ausentes no CHECK/CXIP/STATUS). Quando o valor lido (applied) vem vazio,
 * a diferença é ignorada para não gerar falso negativo na checagem — o valor pode ter sido
 * aplicado corretamente mesmo sem ser lido de volta.
 *
 * - sms: comando MG — não é refletido no CHECK
 * - password: segurança — não pode ser lido de volta
 * - clear_buffer / clear_horimeter: ações pontuais, sem estado persistente
 * - ip_secondary: o device pode não devolver o IP2 na leitura (CXIP/CHECK)
 */
const BWS4G_WRITE_ONLY_FIELDS = new Set([
  'sms',
  'password',
  'clear_buffer',
  'clear_horimeter',
  'ip_secondary',
]);

function isEmptyApplied(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === 'object' && Object.keys(value as object).length === 0) return true;
  return false;
}

/**
 * Filtra diferenças irrelevantes de uma checagem de configuração:
 *
 * 1. Ignora campos cujo valor desejado é objeto vazio ({}) quando o aplicado também é vazio.
 * 2. Para campos write-only do BWS 4G (ver BWS4G_WRITE_ONLY_FIELDS), ignora a diferença
 *    quando o valor lido (applied) vier vazio — o device não devolve esses campos.
 *
 * A chave pode vir com prefixo de caminho (ex.: "ip_secondary.port"); a raiz é comparada
 * contra a lista de write-only.
 */
export function filterIrrelevantDiffs(diffs: DiffResult[]): DiffResult[] {
  return diffs.filter((diff) => {
    // 1) desired === {} e applied vazio → ruído de objeto vazio
    if (
      diff.desired !== undefined &&
      typeof diff.desired === 'object' &&
      Object.keys(diff.desired).length === 0 &&
      isEmptyApplied(diff.applied)
    ) {
      return false;
    }

    // 2) campo write-only do BWS 4G com leitura vazia → não conta como divergência
    const rootKey = diff.key.split('.')[0];
    if (BWS4G_WRITE_ONLY_FIELDS.has(rootKey) && isEmptyApplied(diff.applied)) {
      return false;
    }

    return true;
  });
}

export function diffObjects({
  desired,
  applied,
  keySelection = 'desired',
  prefix = '',
}: {
  desired: Record<string, any>;
  applied: Record<string, any>;
  keySelection?: 'desired' | 'applied';
  prefix?: string;
}): DiffResult[] {
  const diffs: DiffResult[] = [];

  // Use keys from the selected object
  const keys = keySelection === 'desired' ? Object.keys(desired) : Object.keys(applied);

  for (const key of keys) {
    const desiredValue = desired[key];
    const appliedValue = applied[key];
    const fullKey = prefix ? `${prefix}.${key}` : key;

    // If both are objects (and not null), recurse
    if (
      desiredValue &&
      appliedValue &&
      typeof desiredValue === 'object' &&
      typeof appliedValue === 'object' &&
      !Array.isArray(desiredValue) &&
      !Array.isArray(appliedValue)
    ) {
      diffs.push(
        ...diffObjects({
          desired: desiredValue,
          applied: appliedValue,
          keySelection,
          prefix: fullKey,
        }),
      );
    } else if (JSON.stringify(desiredValue) !== JSON.stringify(appliedValue)) {
      diffs.push({
        key: fullKey,
        desired: desiredValue,
        applied: appliedValue,
      });
    }
  }

  return diffs;
}
